import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminKeyGate from '../components/AdminKeyGate';
import { setAdminKey, listAdminRaffles, createAdminRaffle, getAdminRaffle,
  openAdminRaffle, closeAdminRaffle, drawAdminRaffle, deleteAdminRaffle } from '../api';
import { raffleEntryUrl } from '../raffle/qr';
import { printRafflePoster } from '../raffle/poster';
import './AdminRafflesPage.css';

const isAuth = error => [401, 403].includes(error?.response?.status);
const explain = (error, fallback) => error?.response?.data?.error ||
  (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
    ? 'Request timed out. Check the refreshed server state before retrying.'
    : fallback);

function readKey() {
  const key = sessionStorage.getItem('f1tips_adminkey') || '';
  setAdminKey(key);
  return key;
}

export default function AdminRafflesPage() {
  const [key, setKey] = useState(readKey);
  return key ? <RaffleDashboard key={key} sessionKey={key} onDenied={() => {
    // This dashboard may already be obsolete after a separate session change.
    if (sessionStorage.getItem('f1tips_adminkey') !== key) return;
    sessionStorage.removeItem('f1tips_adminkey'); setAdminKey(null); setKey('');
  }} /> : <AdminKeyGate onKey={setKey} />;
}

function RaffleDashboard({ onDenied, sessionKey }) {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [prize, setPrize] = useState('');
  const [posterTitle, setPosterTitle] = useState('');
  const [posterPrize, setPosterPrize] = useState('');
  const [deleteText, setDeleteText] = useState('');
  const [pending, setPending] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copyError, setCopyError] = useState('');
  const request = useRef(null);
  const actionRequest = useRef(null);
  const actionEpoch = useRef(0);
  const mounted = useRef(false);
  const sessionActive = useCallback(() => mounted.current &&
    sessionStorage.getItem('f1tips_adminkey') === sessionKey, [sessionKey]);
  const actionLock = useRef(false);
  const sequence = useRef(0);
  const selected = useRef(null);
  selected.current = selectedId;

  const load = useCallback(async (id = selected.current, { signal } = {}) => {
    const [list, info] = await Promise.all([
      listAdminRaffles({ signal }), id ? getAdminRaffle(id, { signal }) : Promise.resolve(null),
    ]);
    return { list, info };
  }, []);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    const seq = ++sequence.current;
    setLoading(true); setError('');
    // One selected detail per route/ID: abort old reads and reject late completions.
    load(selectedId, { signal: controller.signal }).then(({ list, info }) => {
      if (controller.signal.aborted || sequence.current !== seq || !sessionActive()) return;
      setItems(list); setDetail(info);
      if (info) { setPosterTitle(info.title); setPosterPrize(info.prize || ''); }
      setLoading(false);
    }).catch(e => {
      if (controller.signal.aborted || sequence.current !== seq || !sessionActive()) return;
      if (isAuth(e)) return onDenied();
      setError(explain(e, 'Unable to load raffles.')); setLoading(false);
    });
    return () => controller.abort();
  }, [selectedId, load, onDenied, sessionActive]);

  const refresh = useCallback(async () => {
    const seq = ++sequence.current;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    try {
      const { list, info } = await load(selected.current, { signal: controller.signal });
      if (controller.signal.aborted || seq !== sequence.current || !sessionActive()) return;
      setItems(list); setDetail(info); setLoading(false);
      if (info) { setPosterTitle(info.title); setPosterPrize(info.prize || ''); }
      else if (selected.current) setSelectedId(null);
    } catch (e) {
      if (controller.signal.aborted || seq !== sequence.current || !sessionActive()) return;
      if (isAuth(e)) return onDenied();
      setError(explain(e, 'Unable to refresh raffle data.'));
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, [load, onDenied, sessionActive]);

  useEffect(() => () => {
    mounted.current = false;
    sequence.current += 1;
    actionEpoch.current += 1;
    request.current?.abort();
    actionRequest.current?.abort();
  }, []);

  // Single-flight POSTs can commit even if aborted; never assume cancellation
  // rolled back the server. The next mount reloads the persisted result.
  const act = async (kind, task, after) => {
    if (actionLock.current || !sessionActive()) return;
    actionLock.current = true;
    const controller = new AbortController();
    const epoch = ++actionEpoch.current;
    actionRequest.current = controller;
    const active = () => sessionActive() && !controller.signal.aborted &&
      actionRequest.current === controller && actionEpoch.current === epoch;
    setPending(kind); setError(''); setNotice('');
    let revoked = false;
    try {
      const result = await task(controller.signal);
      if (!active()) return;
      if (after) after(result);
      setNotice(`${kind} — server confirmed.`);
    } catch (e) {
      if (!active()) return;
      if (isAuth(e)) { revoked = true; onDenied(); }
      else setError(explain(e, `${kind} failed. Server state will be refreshed.`));
    } finally {
      if (active() && !revoked) {
        await refresh();
        if (active()) setPending('');
      }
      if (actionRequest.current === controller) actionRequest.current = null;
      actionLock.current = false;
    }
  };

  const create = e => {
    e.preventDefault();
    if (!title.trim() || pending) return;
    act('Create', signal => createAdminRaffle({ title, description, prize }, { signal }), result => {
      selected.current = result.id; setSelectedId(result.id); setTitle(''); setDescription(''); setPrize('');
    });
  };

  const selectedState = detail?.state;
  const entryUrl = detail ? raffleEntryUrl(detail.id) : '';
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(entryUrl); setCopyError('Link copied.'); }
    catch { setCopyError(`Could not copy automatically. Select the URL below: ${entryUrl}`); }
  };
  const print = () => {
    if (!printRafflePoster(detail, { title: posterTitle, prize: posterPrize })) {
      setError('Poster popup was blocked. Allow popups for this site and try again.');
    } else { setNotice('Poster opened. Review it, then use your browser’s Print command (Ctrl+P).'); }
  };

  return <main className="raffle-admin">
    <div className="raffle-admin-heading"><div><Link to="/admin">← Admin</Link><h1>Event raffles</h1><p>One random winner · No race code · One email, one chance</p></div>
      <button type="button" onClick={refresh} disabled={!!pending}>Reload server state</button></div>
    {error && <p role="alert" className="raffle-admin-error">{error}</p>}
    {notice && <p role="status" className="raffle-admin-notice">{notice}</p>}
    <div className="raffle-admin-grid"><section className="raffle-admin-panel">
      <h2>Create raffle</h2><form onSubmit={create} className="raffle-admin-form">
        <label>Title <input required maxLength={160} value={title} onChange={e=>setTitle(e.target.value)} placeholder="Event title" /></label>
        <label>Description (optional) <input maxLength={1000} value={description} onChange={e=>setDescription(e.target.value)} placeholder="Single line, max 1000" /></label>
        <label>Prize (optional) <input maxLength={240} value={prize} onChange={e=>setPrize(e.target.value)} placeholder="Prize" /></label>
        <button className="raffle-admin-primary" disabled={!!pending || !title.trim()}>Create draft</button>
      </form><h2>Raffles</h2>
      {loading && <p role="status">Loading raffles…</p>}
      {!loading && !error && !items.length && <p>No raffles found.</p>}
      <div className="raffle-admin-list">{items.map(item=><button type="button" key={item.id} className={item.id===selectedId?'selected':''} onClick={()=>{ if(!pending) {selected.current=item.id;setSelectedId(item.id);setDetail(null);setDeleteText('');setError('');setNotice('');} }} disabled={!!pending}>
        <strong>{item.title}</strong><span>{item.state} · {item.entry_count} entries</span></button>)}</div>
    </section>
    <section className="raffle-admin-panel" aria-label="Selected raffle">
      {!detail ? <p>{selectedId ? (loading ? 'Loading raffle…' : 'Select reload to retrieve raffle details.') : 'Select a raffle or create a draft.'}</p> : <>
        <div className="raffle-admin-detail-head"><h2>{detail.title}</h2><span className="raffle-admin-state">{selectedState}</span></div>
        <p>{detail.description}</p><p>Prize: <strong>{detail.prize || 'Not specified'}</strong></p>
        <p><strong>{detail.entry_count} registrations</strong></p>
        <div className="raffle-admin-actions">
          {selectedState==='draft' && <button className="raffle-admin-primary" disabled={!!pending} onClick={()=>{
            if(window.confirm('Open entries for this raffle? You cannot return to draft.')) act('Open',signal=>openAdminRaffle(detail.id, { signal }));
          }}>Open entries</button>}
          {selectedState==='open' && <button disabled={!!pending} onClick={()=>{
            if(window.confirm('Close entries permanently? No more entries can be accepted.')) act('Close',signal=>closeAdminRaffle(detail.id, { signal }));
          }}>Close entries</button>}
          {selectedState==='closed' && detail.entry_count>0 && <button className="raffle-admin-primary" disabled={!!pending} onClick={()=>{
            if(window.confirm('Draw the ONE permanent winner now? There is no reroll.')) act('Draw',signal=>drawAdminRaffle(detail.id, { signal }));
          }}>Draw winner · permanent</button>}
          {selectedState==='closed' && detail.entry_count===0 && <p>Cannot draw: no registrations.</p>}
          {pending && <p role="status">{pending} in progress…</p>}
        </div>
        {detail.draw && <div className="raffle-admin-winner"><h3>Saved winner</h3><strong>{detail.draw.winner_name}</strong><p>Organiser contact: {detail.draw.winner_email}</p><small>Drawn: {new Date(detail.draw.drawn_at).toLocaleString()} · Eligible entries: {detail.draw.eligible_count}</small></div>}
        <h3>Guest link and display</h3>
        <p className="raffle-admin-url" data-testid="raffle-entry-url">{entryUrl}</p>
        <div className="raffle-admin-actions"><button onClick={copyLink} disabled={!!pending}>Copy entry link</button>
          <Link className="raffle-admin-link" to={`/rafflewall/${detail.id}`} target="_blank" rel="noopener noreferrer">Open Raffle Pitwall ↗</Link></div>
        {copyError && <p role="status">{copyError}</p>}
        <h3>Print A4 QR poster</h3>
        <div className="raffle-admin-form"><label>Poster title <input maxLength={160} value={posterTitle} onChange={e=>setPosterTitle(e.target.value)} /></label>
        <label>Poster prize <input maxLength={240} value={posterPrize} onChange={e=>setPosterPrize(e.target.value)} /></label>
        <button onClick={print}>Print poster</button><small>Print window uses this app’s current origin (local preview stays local).</small></div>
        <div className="raffle-admin-danger"><h3>Permanent removal</h3><p>Deleting removes all entries and the saved winner. No automatic retention/deletion is configured.</p>
          <label>Type the exact raffle title to confirm deletion <input value={deleteText} onChange={e=>setDeleteText(e.target.value)} /></label>
          <button disabled={!!pending || deleteText !== detail.title} onClick={()=>{
            if(window.confirm(`Permanently delete “${detail.title}”, all entries and the saved draw?`)) act('Delete',signal=>deleteAdminRaffle(detail.id,deleteText, { signal }),()=>{selected.current=null;setSelectedId(null);setDetail(null);setDeleteText('');});
          }}>Delete permanently</button></div>
      </>}
    </section></div>
  </main>;
}
